import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { COLORS, FONTS } from "@/lib/constants";
import { api } from "@/lib/api";
import { useResponsive } from "@/lib/responsive";
import ThemeToggle from "@/components/ThemeToggle";
import { MobileHeader, MobileHeaderButton } from "@/components/mobile/MobileHeader";

const SLOTS = [
  { key: "food",   color: "#2ECC8A", rgb: "var(--plan-food-rgb)", text: "var(--lit-green-8)", label: "Green",  hint: "meals / restaurants",   placeholder: "Weekly groceries · Sunday dinner …",   itemPlaceholder: "Bread, milk, olive oil…" },
  { key: "hobby",  color: "#7B68EE", rgb: "var(--plan-hobby-rgb)", text: "var(--lit-purple-17)", label: "Purple", hint: "health / cosmetology",  placeholder: "Dermatologist visit · pilates pass …", itemPlaceholder: "Facial, supplement, class…" },
  { key: "subs",   color: "#4A90D9", rgb: "var(--plan-subs-rgb)", text: "var(--lit-blue-19)", label: "Blue",   hint: "subscriptions / games / hobby", placeholder: "Netflix · Steam · pottery class …", itemPlaceholder: "Service, game — monthly cost" },
  { key: "idea",   color: "#FF7F6E", rgb: "var(--plan-idea-rgb)", text: "var(--lit-coral-12)", label: "Coral",  hint: "gift / idea / dream",   placeholder: "Short film · gift for M · dream trip", itemPlaceholder: "Camera rental, editor, coffee…" },
  { key: "travel", color: "#EBC562", rgb: "var(--plan-travel-rgb)", text: "var(--lit-yellow-21)", label: "Yellow", hint: "travel / culture",      placeholder: "Weekend in Porto · theatre night",      itemPlaceholder: "Flight, hotel, ticket, meal…" },
];

const meta = (k) => SLOTS.find((s) => s.key === k) || SLOTS[0];

function toGCalUrl(p, total) {
  // Fall back to "now" if the user hasn't picked a datetime yet — Google
  // Calendar lets them edit it in the pre-filled event before saving. This
  // guarantees the button always leads somewhere (important on iPad where
  // "does nothing" is confusing).
  const start = p.event_at ? new Date(p.event_at) : new Date();
  if (Number.isNaN(start.getTime())) return null;
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  const fmt = (d) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const itemsList = p.items.map((i) => `• ${i.name || "—"}  ${i.price ? `€${i.price}` : ""}`).join("\n");
  const details = [
    `Category: ${meta(p.color_key).label} · ${meta(p.color_key).hint}`,
    `Budget: €${total.toFixed(2)}`,
    p.feeling ? `Feeling: ${p.feeling}` : "",
    itemsList ? `\nItems:\n${itemsList}` : "",
  ].filter(Boolean).join("\n");
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: p.title || meta(p.color_key).label,
    dates: `${fmt(start)}/${fmt(end)}`,
    details,
    location: p.location || "",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export default function Planning() {
  const navigate = useNavigate();
  const { readingMaxWidth, gutter, isPhone } = useResponsive();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [creating, setCreating] = useState(null);
  const [titleDraft, setTitleDraft] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.planningList();
      setPlans(r?.plans || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createPlan = async () => {
    if (!creating || !titleDraft.trim()) return;
    try {
      const doc = await api.planningCreate({
        color_key: creating,
        title: titleDraft.trim(),
        items: [],
        done: false,
        feeling: "",
      });
      setPlans((prev) => [doc, ...prev]);
      setExpanded(doc.plan_id);
    } finally {
      setCreating(null);
      setTitleDraft("");
    }
  };

  const patch = async (id, patchData) => {
    setPlans((prev) => prev.map((p) => (p.plan_id === id ? { ...p, ...patchData } : p)));
    try { await api.planningUpdate(id, patchData); } catch { /* silent */ }
  };

  const remove = async (id) => {
    setPlans((prev) => prev.filter((p) => p.plan_id !== id));
    try { await api.planningDelete(id); } catch { /* silent */ }
  };

  return (
    <div data-testid="planning-screen" style={{ minHeight: "100vh", background: "var(--yuo-page-planning)" }}>
      {isPhone && (
        <MobileHeader room="symbiosis" title="Content" sub="planning" testId="planning-m-header"
          actions={<>
            <ThemeToggle compact />
            <MobileHeaderButton room="symbiosis" onClick={() => navigate(-1)} testId="planning-back">Close</MobileHeaderButton>
          </>} />
      )}
      {!isPhone && (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: `14px ${gutter}px`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{
            width: 12, height: 12, borderRadius: "50%",
            background: "radial-gradient(38% 34% at 34% 26%, #fff, #34FF96 20%, #0D3A26 60%, #05060c 100%)",
            boxShadow: "0 0 10px rgba(var(--role-green-rgb),0.6)",
          }} />
          <div style={{ color: "var(--yuo-ink)", fontSize: 13, letterSpacing: "0.3em", textTransform: "uppercase" }}>
            Content
          </div>
          <div style={{ color: "var(--yuo-muted-36)", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase" }}>
            · Planning
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <ThemeToggle />
        <button onClick={() => navigate(-1)} data-testid="planning-back"
          style={{
            background: "var(--yuo-surface-5)", color: "var(--yuo-ink)",
            border: "none", borderRadius: 999, padding: "8px 18px",
            fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
            fontFamily: FONTS.body, cursor: "pointer",
          }}>
          Close
        </button>
        </div>
      </div>
      )}

      <div style={{ padding: isPhone ? "8px 14px 100px" : `20px ${gutter}px 80px` }}>
        <div style={{ maxWidth: readingMaxWidth, margin: "0 auto" }}>
          <div
            style={{
              color: COLORS.textMuted,
              fontFamily: FONTS.body,
              fontSize: isPhone ? 10 : 11,
              letterSpacing: 1.5,
              textTransform: "uppercase",
              marginBottom: 10,
            }}
          >
            Color palette · tap a color to start a plan
          </div>

          <div data-testid="planning-palette" style={isPhone
            ? { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 18 }
            : { display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 20 }}>
            {SLOTS.map((s, idx) => {
              const rgb = s.rgb;
              return (
                <button
                  key={s.key}
                  onClick={() => { setCreating(s.key); setTitleDraft(""); }}
                  data-testid={`planning-color-${s.key}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    border: "none",
                    borderRadius: 999,
                    padding: isPhone ? "8px 14px" : "9px 18px",
                    minWidth: isPhone ? 0 : 160,
                    minHeight: isPhone ? 44 : undefined,
                    flex: isPhone ? undefined : 1,
                    gridColumn: isPhone && idx === SLOTS.length - 1 ? "1 / -1" : undefined,
                    cursor: "pointer",
                    background: `radial-gradient(120% 160% at 30% 0%, rgba(${rgb},0.26), rgba(${rgb},0.06))`,
                    boxShadow: `inset 0 0 0 1px rgba(${rgb},var(--plan-ring-a)), 0 0 22px rgba(${rgb},0.28)`,
                    textAlign: "left",
                  }}
                >
                  <span style={{
                    width: 14, height: 14, borderRadius: "50%", flex: "none",
                    background: `radial-gradient(38% 34% at 34% 26%, #fff, ${s.color} 30%, rgba(${rgb},0.18) 62%, #05060c 100%)`,
                    boxShadow: `0 0 12px rgba(${rgb},0.7)`,
                  }} />
                  {isPhone ? (
                    <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                      <span style={{ color: COLORS.textPrimary, fontFamily: FONTS.body, fontSize: 11, fontWeight: 600, letterSpacing: 0.2 }}>{s.label}</span>
                      <span style={{ color: COLORS.textMuted, fontFamily: FONTS.body, fontSize: 9.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.hint}</span>
                    </span>
                  ) : (
                    <>
                      <span style={{ color: COLORS.textPrimary, fontFamily: FONTS.body, fontSize: 12, fontWeight: 600, letterSpacing: 0.2 }}>
                        {s.label}
                      </span>
                      <span style={{ color: COLORS.textMuted, fontFamily: FONTS.body, fontSize: 11, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {s.hint}
                      </span>
                    </>
                  )}
                </button>
              );
            })}
          </div>

          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: COLORS.textMuted }}>Loading…</div>
          ) : plans.length === 0 ? (
            <div
              style={{
                color: COLORS.textMuted,
                fontFamily: FONTS.body,
                fontSize: 14,
                textAlign: "center",
                padding: "40px 20px",
                lineHeight: "20px",
              }}
            >
              No plans yet. Tap a color above to start your first one — food, sport, travel, dream, subscription…
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {plans.map((p) => (
                <PlanCard
                  key={p.plan_id}
                  plan={p}
                  phone={isPhone}
                  expanded={expanded === p.plan_id}
                  onToggle={() => setExpanded(expanded === p.plan_id ? null : p.plan_id)}
                  onPatch={(u) => patch(p.plan_id, u)}
                  onDelete={() => remove(p.plan_id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {creating && (
        <div className="yuo-modal-backdrop" onClick={() => setCreating(null)}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              margin: "auto",
              width: isPhone ? "calc(100% - 28px)" : "100%",
              maxWidth: 480,
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 16,
              padding: 18,
            }}
          >
            <div
              style={{
                color: COLORS.textMuted,
                fontFamily: FONTS.body,
                fontSize: 11,
                letterSpacing: 1.5,
                textTransform: "uppercase",
                marginBottom: 8,
              }}
            >
              New plan
            </div>
            <div style={{ color: COLORS.textPrimary, fontFamily: FONTS.heading, fontSize: 18, marginBottom: 12 }}>
              {meta(creating).label} · {meta(creating).hint}
            </div>
            <input
              autoFocus
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createPlan()}
              placeholder={meta(creating).placeholder}
              data-testid="plan-title-input"
              style={{
                width: "100%",
                border: `1px solid ${COLORS.border}`,
                borderRadius: 10,
                padding: 12,
                color: COLORS.textPrimary,
                background: COLORS.bg,
                fontSize: 15,
                fontFamily: FONTS.body,
                outline: "none",
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
              <button
                onClick={() => setCreating(null)}
                style={{ padding: "8px 12px", color: COLORS.textSecondary, fontFamily: FONTS.body, fontSize: 13 }}
              >
                Cancel
              </button>
              <button
                onClick={createPlan}
                data-testid="plan-create-submit"
                style={{
                  background: COLORS.mirror,
                  color: COLORS.bg,
                  padding: "10px 18px",
                  borderRadius: 999,
                  fontFamily: FONTS.body,
                  fontWeight: 700,
                }}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PlanCard({ plan, expanded, onToggle, onPatch, onDelete, phone = false }) {
  const m = meta(plan.color_key);
  const total = useMemo(
    () => plan.items.reduce((s, it) => s + (Number(it.price) || 0), 0),
    [plan.items],
  );
  const [newName, setNewName] = useState("");
  const [newPrice, setNewPrice] = useState("");

  const addItem = () => {
    const name = newName.trim();
    if (!name) return;
    const price = parseFloat((newPrice || "").replace(",", ".")) || 0;
    onPatch({ items: [...plan.items, { name, price }] });
    setNewName("");
    setNewPrice("");
  };
  const updateItem = (i, changes) => {
    onPatch({ items: plan.items.map((it, idx) => (idx === i ? { ...it, ...changes } : it)) });
  };
  const removeItem = (i) => {
    onPatch({ items: plan.items.filter((_, idx) => idx !== i) });
  };

  const gcalUrl = useMemo(() => toGCalUrl(plan, total), [plan, total]);

  return (
    <div style={{ border: "none", background: `radial-gradient(120% 100% at 22% 0%, rgba(${m.rgb},0.14), rgba(${m.rgb},0.03) 62%, transparent 100%)`, boxShadow: `inset 0 0 0 1px rgba(${m.rgb},var(--plan-ring-a))`, borderRadius: 26, overflow: "hidden" }}>
      <button
        onClick={onToggle}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: 14,
          width: "100%",
          textAlign: "left",
        }}
      >
        <span style={{ width: 14, height: 14, borderRadius: 7, background: m.color }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: COLORS.textPrimary, fontFamily: FONTS.heading, fontSize: 16, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {plan.title || m.hint}
          </div>
          <div style={{ color: COLORS.textSecondary, fontFamily: FONTS.body, fontSize: 11, marginTop: 2 }}>
            {m.label} · {m.hint} · €{total.toFixed(2)}
            {plan.done ? "  ·  ✓ done" : ""}
          </div>
        </div>
        <span style={{ color: COLORS.textSecondary, fontSize: 22 }}>{expanded ? "−" : "+"}</span>
      </button>

      {expanded && (
        <div style={{ padding: "12px 14px 14px", borderTop: `1px solid ${COLORS.border}`, display: "flex", flexDirection: "column", gap: 8 }}>
          <input
            value={plan.title}
            onChange={(e) => onPatch({ title: e.target.value })}
            placeholder={m.placeholder}
            style={inputStyle}
          />

          <div style={sectionLabel}>Items · what you plan to buy / book</div>
          {plan.items.map((it, i) => (
            <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input
                value={it.name}
                onChange={(e) => updateItem(i, { name: e.target.value })}
                placeholder={m.itemPlaceholder}
                style={{ ...inputStyle, flex: 3 }}
              />
              <input
                value={String(it.price || "")}
                onChange={(e) => updateItem(i, { price: parseFloat(e.target.value.replace(",", ".")) || 0 })}
                placeholder="€"
                type="text"
                inputMode="decimal"
                style={{ ...inputStyle, flex: 1, textAlign: "right", minWidth: 68 }}
              />
              <button
                onClick={() => removeItem(i)}
                style={{ padding: "4px 8px", color: COLORS.textMuted, fontSize: 18 }}
              >
                ×
              </button>
            </div>
          ))}
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addItem()}
              placeholder="+ add item"
              style={{ ...inputStyle, flex: 3 }}
            />
            <input
              value={newPrice}
              onChange={(e) => setNewPrice(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addItem()}
              placeholder="€"
              inputMode="decimal"
              style={{ ...inputStyle, flex: 1, textAlign: "right", minWidth: 68 }}
            />
            <button
              onClick={addItem}
              style={{ background: m.color, color: "var(--yuo-on-accent)", padding: "8px 12px", borderRadius: 8, fontFamily: FONTS.body, fontWeight: 700, fontSize: 12 }}
            >
              Add
            </button>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 0",
              borderTop: `1px solid ${COLORS.border}`,
              marginTop: 12,
            }}
          >
            <div style={{ color: COLORS.textSecondary, fontFamily: FONTS.body, fontSize: 12, letterSpacing: 1.2, textTransform: "uppercase" }}>
              Total budget
            </div>
            <div style={{ color: m.text, fontFamily: FONTS.heading, fontSize: 20, fontWeight: 700 }}>
              €{total.toFixed(2)}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
            <div style={{ color: COLORS.textPrimary, fontFamily: FONTS.body, fontSize: 13, fontWeight: 600 }}>
              Done
            </div>
            <label style={{ position: "relative", display: "inline-block", width: 40, height: 22 }}>
              <input
                type="checkbox"
                checked={!!plan.done}
                onChange={(e) => onPatch({ done: e.target.checked })}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span
                style={{
                  position: "absolute",
                  cursor: "pointer",
                  inset: 0,
                  background: plan.done ? m.color : COLORS.border,
                  borderRadius: 22,
                  transition: "0.2s",
                }}
              />
              <span
                style={{
                  position: "absolute",
                  height: 16,
                  width: 16,
                  left: plan.done ? 21 : 3,
                  top: 3,
                  background: "var(--yuo-ink-strong)",
                  borderRadius: 8,
                  transition: "0.2s",
                }}
              />
            </label>
          </div>

          <div style={sectionLabel}>How I felt · small note</div>
          <textarea
            value={plan.feeling}
            onChange={(e) => onPatch({ feeling: e.target.value })}
            placeholder={plan.done ? "After I did it — what stayed with me…" : "Right now I feel… (write anything)"}
            rows={3}
            style={{ ...inputStyle, minHeight: 70, lineHeight: "19px" }}
          />

          <div style={sectionLabel}>Schedule · Google Calendar</div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <input
              type="datetime-local"
              value={plan.event_at || ""}
              onChange={(e) => onPatch({ event_at: e.target.value })}
              data-testid={`plan-event-at-${plan.plan_id}`}
              style={{ ...inputStyle, flex: phone ? "1 1 100%" : 3, minWidth: phone ? 0 : 200, maxWidth: phone ? "100%" : undefined }}
            />
            <input
              value={plan.location || ""}
              onChange={(e) => onPatch({ location: e.target.value })}
              placeholder="Location"
              style={{ ...inputStyle, flex: phone ? "1 1 100%" : 1, minWidth: phone ? 0 : 140 }}
            />
          </div>
          <div style={{ color: COLORS.textMuted, fontFamily: FONTS.body, fontSize: 11, marginTop: 4 }}>
            Opens Google Calendar in a new tab — sign in there if prompted, then Save.
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8, alignItems: "center" }}>
            <a
              href={gcalUrl || "#"}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                if (!gcalUrl) return;
                // Belt-and-braces: also navigate programmatically so it
                // works even if the browser blocks target="_blank" (some
                // iPad in-app browsers do). Same-tab navigation always
                // works.
                e.preventDefault();
                const win = window.open(gcalUrl, "_blank", "noopener,noreferrer");
                if (!win) window.location.assign(gcalUrl);
              }}
              data-testid={`plan-gcal-${plan.plan_id}`}
              style={{
                padding: "8px 12px",
                borderRadius: 999,
                border: `1px solid ${m.text}`,
                color: m.text,
                fontFamily: FONTS.body,
                fontSize: 12,
                fontWeight: 700,
                textDecoration: "none",
                display: "inline-block",
                cursor: "pointer",
              }}
            >
              📅 Add to Google Calendar
            </a>
            <div style={{ flex: 1 }} />
            <button
              onClick={onDelete}
              style={{
                padding: "8px 12px",
                borderRadius: 999,
                border: `1px solid ${COLORS.error}`,
                color: COLORS.error,
                fontFamily: FONTS.body,
                fontSize: 12,
              }}
            >
              Delete plan
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 8,
  padding: 8,
  color: COLORS.textPrimary,
  fontFamily: FONTS.body,
  fontSize: 13,
  background: COLORS.bg,
  outline: "none",
  width: "100%",
};

const sectionLabel = {
  color: COLORS.textMuted,
  fontFamily: FONTS.body,
  fontSize: 10,
  letterSpacing: 1.5,
  textTransform: "uppercase",
  marginTop: 8,
};
